import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircleIcon,
  ClockIcon,
  InboxIcon,
  SearchIcon,
  ZapIcon
} from 'lucide-react';
import { AppBar } from '../../components/AppBar';
import { ActionPanel } from '../../components/committee/ActionPanel';
import { usePortal } from '../../contexts/PortalContext';
import * as api from '../../api/research';
import type {
  ActivityRow,
  CommitteeProposalRow,
  WorkBucketRow,
  WorkItemRow
} from '../../api/types';
import { formatDateTime } from '../../data/fieldMap';
import { firstNameOf } from '../../data/roles';

const PAGE_SIZE = 20;

export function CommitteeDashboard() {
  const { sessionToken, config } = usePortal();
  const navigate = useNavigate();

  /* The work queue — what this office is holding up. */
  const [buckets, setBuckets] = useState<WorkBucketRow[]>([]);
  const [items, setItems] = useState<WorkItemRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [roles, setRoles] = useState({ src: false, irb: false });
  const [actorName, setActorName] = useState('');
  const [openBucket, setOpenBucket] = useState<string | null>(null);
  const [acting, setActing] = useState<WorkItemRow | null>(null);

  /* The searchable archive of everything, below the queue. */
  const [rows, setRows] = useState<CommitteeProposalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');

  const [loadingQueue, setLoadingQueue] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    if (!sessionToken) return;
    setLoadingQueue(true);
    try {
      const res = await api.committeeWorkQueue(sessionToken);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setBuckets(res.buckets);
      setItems(res.items);
      setActivity(res.activity);
      setActorName(res.actorName);
      setRoles({ src: res.isSrcAdmin, irb: res.isIrbAdmin });

      /* Open the first bucket that actually has work in it. */
      setOpenBucket((current) => {
        if (current && res.buckets.some((b) => b.BucketKey === current)) return current;
        return res.buckets.find((b) => b.Total > 0)?.BucketKey ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your work queue.');
    } finally {
      setLoadingQueue(false);
    }
  }, [sessionToken]);

  const loadList = useCallback(async () => {
    if (!sessionToken) return;
    setLoadingList(true);
    try {
      const res = await api.committeeProposals({
        SessionToken: sessionToken,
        StatusId: statusFilter || null,
        Search: search || null,
        Page: page,
        PageSize: PAGE_SIZE
      });
      if (!res.ok) setError(res.message);
      else {
        setRows(res.rows);
        setTotal(res.total);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the proposal list.');
    } finally {
      setLoadingList(false);
    }
  }, [page, search, sessionToken, statusFilter]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const statuses = config?.statuses ?? [];
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const totalWaiting = buckets.reduce((sum, b) => sum + b.Total, 0);
  const totalStale = buckets.reduce((sum, b) => sum + b.Waiting14Plus, 0);
  const overdueReviews = items.reduce((sum, i) => sum + i.ReviewsOverdue, 0);
  const shownItems = items.filter((i) => i.BucketKey === openBucket);
  const shownBucketTotal = buckets.find((b) => b.BucketKey === openBucket)?.Total ?? 0;

  return (
    <div className="page">
      <AppBar showHome={false} />
      <main className="container section-gap">
        <div className="stack">
          <section className="hero">
            <div>
              <h1>{roles.irb ? 'IRB Office' : 'Scientific Research Committee Office'}</h1>
              <p>
                {loadingQueue
                  ? 'Loading your work queue…'
                  : totalWaiting === 0
                    ? `Nothing is waiting on your office, ${firstNameOf(actorName) || 'colleague'}.`
                    : `${totalWaiting} proposal${totalWaiting === 1 ? '' : 's'} waiting on your office.` +
                      (totalStale > 0 ? ` ${totalStale} have been waiting over a fortnight.` : '')}
              </p>
            </div>
          </section>

          {error ? (
            <div className="notice notice--error" role="alert">
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          ) : null}

          <section className="status-strip" aria-label="Workload">
            <div className="status-cell">
              <span className="status-key">Waiting on you</span>
              <span className="status-value">{totalWaiting}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Over 14 days</span>
              <span className="status-value">{totalStale}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">Overdue reviews</span>
              <span className="status-value">{overdueReviews}</span>
            </div>
            <div className="status-cell">
              <span className="status-key">In the pipeline</span>
              <span className="status-value">{total}</span>
            </div>
          </section>

          {/* ---------------------------------------------------- work queue */}
          <section className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Needs your attention</h2>
                <p className="card-subtitle">
                  Grouped by the action each one is waiting for. You can act from here —
                  opening the proposal is only needed to read it.
                </p>
              </div>
              <span className="badge badge--blue">
                <ZapIcon size={12} aria-hidden="true" />
                {totalWaiting} open
              </span>
            </div>

            <div className="card-body">
              {loadingQueue ? (
                <p className="table-empty">Loading…</p>
              ) : buckets.length === 0 ? (
                <p className="table-empty">Your account is not attached to a committee office.</p>
              ) : (
                <>
                  <ul className="bucket-row">
                    {buckets.map((bucket) => {
                      const active = bucket.BucketKey === openBucket;
                      return (
                        <li key={bucket.BucketKey}>
                          <button
                            type="button"
                            className={`bucket${active ? ' bucket--active' : ''}${
                              bucket.Total === 0 ? ' bucket--empty' : ''
                            }`}
                            aria-pressed={active}
                            onClick={() => setOpenBucket(bucket.BucketKey)}
                          >
                            <span className="bucket-total">{bucket.Total}</span>
                            <span className="bucket-label">{bucket.Label}</span>
                            {bucket.Waiting14Plus > 0 ? (
                              <span className="bucket-flag">
                                <ClockIcon size={11} aria-hidden="true" />
                                {bucket.Waiting14Plus} over 14 days
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {openBucket ? (
                    <p className="field-hint" style={{ marginTop: 'var(--space-3)' }}>
                      {buckets.find((b) => b.BucketKey === openBucket)?.Hint}
                      {/* The queue shows the longest-waiting few, not the whole
                          backlog — say so rather than letting the count and the
                          row tally silently disagree. */}
                      {shownBucketTotal > shownItems.length
                        ? ` Showing the ${shownItems.length} longest waiting of ${shownBucketTotal} — use All proposals below for the rest.`
                        : ''}
                    </p>
                  ) : null}

                  <div className="table-scroll" style={{ marginTop: 'var(--space-3)' }}>
                    <table className="data-table" style={{ minWidth: 900 }}>
                      <thead>
                        <tr>
                          <th scope="col">IRB no.</th>
                          <th scope="col" style={{ minWidth: 280 }}>
                            Title
                          </th>
                          <th scope="col">Investigator</th>
                          <th scope="col">Waiting</th>
                          <th scope="col">Reviews</th>
                          <th scope="col" className="col-action">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {shownItems.length === 0 ? (
                          <tr>
                            <td colSpan={6}>
                              <p className="table-empty">
                                <InboxIcon size={16} aria-hidden="true" /> Nothing in this
                                queue.
                              </p>
                            </td>
                          </tr>
                        ) : (
                          shownItems.map((item) => (
                            <tr key={item.ResearchCode}>
                              <td>
                                <button
                                  type="button"
                                  className="link-button"
                                  onClick={() => navigate(`/committee/${item.ResearchCode}`)}
                                >
                                  {item.IrbNumber ?? `#${item.ResearchCode}`}
                                </button>
                              </td>
                              <td>{item.Title || <em className="muted">Untitled</em>}</td>
                              <td>
                                {item.PiName || item.ResearcherName}
                                <br />
                                <span className="muted">{item.ResearcherEmail}</span>
                              </td>
                              <td>
                                <span className={item.DaysWaiting > 14 ? 'text-warn' : undefined}>
                                  {item.DaysWaiting} day{item.DaysWaiting === 1 ? '' : 's'}
                                </span>
                              </td>
                              <td>
                                {item.ReviewersAssigned === 0 ? (
                                  <span className="muted">—</span>
                                ) : (
                                  <>
                                    {item.ReviewsCompleted}/{item.ReviewersAssigned}
                                    {item.ReviewsOverdue > 0 ? (
                                      <span className="text-warn">
                                        {' '}
                                        · {item.ReviewsOverdue} overdue
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </td>
                              <td className="col-action">
                                <button
                                  type="button"
                                  className="btn btn--primary btn--small"
                                  onClick={() => setActing(item)}
                                >
                                  {buckets.find((b) => b.BucketKey === item.BucketKey)?.ActionLabel ??
                                    'Act'}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* ------------------------------------------------- recent activity */}
          {activity.length > 0 ? (
            <section className="card">
              <div className="card-header">
                <div>
                  <h2 className="card-title">Recent activity</h2>
                  <p className="card-subtitle">The last dozen moves across the pipeline.</p>
                </div>
              </div>
              <div className="card-body">
                <ul className="history-list">
                  {activity.map((row, index) => (
                    <li key={`${row.ResearchCode}-${row.ActionedOn}-${index}`}>
                      <span className="history-when">{formatDateTime(row.ActionedOn)}</span>
                      <span className="history-what">
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => navigate(`/committee/${row.ResearchCode}`)}
                        >
                          {row.IrbNumber ?? `#${row.ResearchCode}`}
                        </button>{' '}
                        <strong>{row.StatusDescription || row.StatusCode}</strong>
                        {row.ActionedBy ? ` · ${row.ActionedBy}` : ''}
                        {row.Title ? <span className="history-remark">{row.Title}</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ) : null}

          {/* ------------------------------------------------ searchable archive */}
          <section className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">All proposals</h2>
                <p className="card-subtitle">
                  Everything your office can see — filter by status, or search by title,
                  investigator, IRB number or code.
                </p>
              </div>
            </div>

            <div className="card-body">
              <div className="filter-bar">
                <label className="sr-only" htmlFor="status-filter">
                  Filter by status
                </label>
                <select
                  id="status-filter"
                  className="input"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  style={{ maxWidth: 320 }}
                >
                  <option value="">All statuses</option>
                  {statuses
                    .filter((s) => s.StatusId !== '01')
                    .map((s) => (
                      <option key={s.StatusId} value={s.StatusId}>
                        {s.StatusId} — {s.Description}
                      </option>
                    ))}
                </select>

                <form
                  className="inline-actions"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setSearch(pendingSearch.trim());
                    setPage(1);
                  }}
                >
                  <label className="sr-only" htmlFor="proposal-search">
                    Search proposals
                  </label>
                  <input
                    id="proposal-search"
                    className="input"
                    placeholder="Title, investigator, IRB no…"
                    value={pendingSearch}
                    onChange={(e) => setPendingSearch(e.target.value)}
                  />
                  <button type="submit" className="btn btn--secondary">
                    <SearchIcon size={15} aria-hidden="true" />
                    Search
                  </button>
                </form>
              </div>

              <div className="table-scroll" style={{ marginTop: 'var(--space-4)' }}>
                <table className="data-table" style={{ minWidth: 980 }}>
                  <thead>
                    <tr>
                      <th scope="col">IRB no.</th>
                      <th scope="col" style={{ minWidth: 280 }}>
                        Title
                      </th>
                      <th scope="col">Investigator</th>
                      <th scope="col">Category</th>
                      <th scope="col">Status</th>
                      <th scope="col">Reviews</th>
                      <th scope="col">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingList ? (
                      <tr>
                        <td colSpan={7}>
                          <p className="table-empty">Loading…</p>
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={7}>
                          <p className="table-empty">Nothing matches these filters.</p>
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr
                          key={row.ResearchCode}
                          className="row-clickable"
                          onClick={() => navigate(`/committee/${row.ResearchCode}`)}
                        >
                          <td>
                            <button
                              type="button"
                              className="link-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/committee/${row.ResearchCode}`);
                              }}
                            >
                              {row.IrbNumber ?? `#${row.ResearchCode}`}
                            </button>
                          </td>
                          <td>{row.Title || <em className="muted">Untitled</em>}</td>
                          <td>
                            {row.PiName || row.ResearcherName}
                            <br />
                            <span className="muted">{row.ResearcherEmail}</span>
                          </td>
                          <td>{row.ReviewCategory ?? '—'}</td>
                          <td>
                            <span className="badge badge--blue">{row.StatusDescription}</span>
                          </td>
                          <td>
                            {row.ReviewersAssigned === 0
                              ? '—'
                              : `${row.ReviewsCompleted}/${row.ReviewersAssigned}`}
                          </td>
                          <td className="muted">{formatDateTime(row.SubmitDateTime)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {pages > 1 ? (
                <div className="inline-actions" style={{ marginTop: 'var(--space-4)' }}>
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="muted">
                    Page {page} of {pages}
                  </span>
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    disabled={page >= pages}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </div>
      </main>

      {acting && sessionToken ? (
        <ActionPanel
          item={acting}
          sessionToken={sessionToken}
          isSrcAdmin={roles.src}
          isIrbAdmin={roles.irb}
          onClose={() => setActing(null)}
          onDone={() => {
            void loadQueue();
            void loadList();
          }}
          onOpenProposal={() => navigate(`/committee/${acting.ResearchCode}`)}
        />
      ) : null}
    </div>
  );
}
